import React, { useEffect, useRef, useState } from 'react';

import { type InviteReturn, useGmailInvite, type UseGmailInviteResult } from '../hooks/useGmailInvite';
import { errorMessage } from '../services/apiError';
import {
  BUSY_STATUSES,
  gmailInviteService,
  GmailInviteBatch,
  GmailInviteDaily,
  GmailInvitePreview,
  GmailInviteStatus,
  InviteSource,
  InviteTarget,
} from '../services/gmailInviteService';

import { invitedNoOne, NoEmailsFound, PermissionHelp } from './GmailInviteHelp';
import { InviteEmailCard } from './InviteEmailCard';
import { CREDITS_PER_FRIEND, WayReward } from './ReferralProgram';

import './InvitePeople.css';
import './GmailInviteAll.css';

const DAY_MS = 86_400_000;

const invites = (count: number): string => `${count.toLocaleString()} ${count === 1 ? 'invite' : 'invites'}`;

const sender = (batch: GmailInviteBatch): string => batch.sender_email ?? 'your Gmail';

/** Invites out so far; a dry run (local test mode) builds them instead and reads the same as prod. */
const sentCount = (batch: GmailInviteBatch): number => (batch.dry_run ? batch.built_count : batch.sent_count);

/** An Invite all from the last 24 hours, whose outcome is still worth showing. */
const recentInviteAll = (batch: GmailInviteBatch | null): GmailInviteBatch | null => (
  batch?.mode === 'send' && Date.now() - Date.parse(batch.created_at ?? '') < DAY_MS ? batch : null
);

const completedLine = (batch: GmailInviteBatch): string => {
  const failed = batch.failed_count ? ` ${batch.failed_count.toLocaleString()} could not be sent.` : '';

  return `${invites(sentCount(batch))} sent from ${sender(batch)}.${failed}`;
};

/** What the last Invite all did; a failure's reason shows as its error, and inviting no one as its own card. */
export const outcomeLine = (batch: GmailInviteBatch): string | null => {
  if (invitedNoOne(batch)) return null;
  if (batch.status === 'completed') return completedLine(batch);
  if (batch.status === 'stopped') return `${invites(sentCount(batch))} sent, then sending stopped.`;
  if (batch.status === 'interrupted') {
    return `Sending stopped after ${invites(sentCount(batch))}. Press Invite all again to invite the rest.`;
  }

  return null;
};

/** Reading the contacts, then sending one invite after another. */
const Progress: React.FC<{ batch: GmailInviteBatch }> = ({ batch }) => {
  if (batch.status === 'collecting') {
    return (
      <p aria-live="polite" className="refer-gmail__status">
        Reading your Google contacts… {batch.contacts_read.toLocaleString()} read so far.
      </p>
    );
  }
  const done = batch.dry_run ? batch.built_count : batch.sent_count + batch.failed_count;
  const width = `${Math.round((100 * done) / Math.max(1, batch.invite_count))}%`;

  return (
    <div aria-live="polite" className="refer-gmail__status">
      <div className="invite-progress"><i style={{ width }} /></div>
      <p>
        Sending from {sender(batch)}: {done.toLocaleString()} of{' '}
        {batch.invite_count.toLocaleString()}.
      </p>
    </div>
  );
};

const nextRun = (iso: string | null): string => (iso ? new Date(iso).toLocaleString(undefined, {
  weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
}) : 'soon');

/** The row's one line: the benefit (with Every day, the daily batches too), today's limit, or the next batch. */
const gmailLine = (status: GmailInviteStatus, daily: GmailInviteDaily | null, everyDay: boolean): string => {
  const left = status.invite_all_remaining;
  const max = status.invite_all_max;

  if (daily?.enabled) return `Every day is on. The next ${max} go out ${nextRun(daily.next_run_at)}.`;
  if (left === 0) {
    return everyDay ? `You sent today's ${max} invites. Press Invite all to send ${max} more each day.`
      : `You sent today's ${max} invites. Send more tomorrow.`;
  }
  const upTo = left < max ? `${left} more` : `${left}`;

  return everyDay ? `Invite ${upTo} contacts now, then ${max} each day.` : `Invite up to ${upTo} contacts now.`;
};

const useSampleEmail = () => {
  const [preview, setPreview] = useState<GmailInvitePreview | null>(null);
  const [open, setOpen] = useState(false);
  const toggle = () => {
    setOpen((value) => !value);
    if (!preview) {
      void gmailInviteService.samplePreview({ projectId: null, role: 'reader' }).then(setPreview).catch(() => undefined);
    }
  };

  return { open, preview, toggle };
};

/** Daily invites as the row knows them: from the status, then from the answer to Stop. */
const useDailyInvites = (initial: GmailInviteDaily | null) => {
  const [daily, setDaily] = useState(initial);
  const [stopping, setStopping] = useState(false);
  const [stopError, setStopError] = useState<string | null>(null);
  const stop = (onStopped: () => void) => {
    setStopping(true);
    setStopError(null);
    void gmailInviteService.stopDaily().then((next) => { setDaily(next); onStopped(); })
      .catch((err) => setStopError(errorMessage(err, 'Daily invites could not be turned off. Try again.')))
      .finally(() => setStopping(false));
  };

  return { daily, stop, stopError, stopping };
};

const MailIcon: React.FC = () => (
  <svg aria-hidden="true" fill="none" viewBox="0 0 20 20">
    <rect height="12" rx="2.5" width="15" x="2.5" y="4" />
    <path d="m3.2 5.4 6.8 5.2 6.8-5.2" />
  </svg>
);

/** Choose people invites them to join 8080.AI with the referral link, never to a project. */
const PLATFORM: InviteTarget = { projectId: null, role: 'reader' };

/** Closes a menu on a press outside it, or on Escape. */
const useMenuDismiss = (open: boolean, setOpen: (open: boolean) => void) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPress = (event: MouseEvent) => { if (!ref.current?.contains(event.target as Node)) setOpen(false); };
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false); };

    document.addEventListener('mousedown', onPress);
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('mousedown', onPress);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, setOpen]);

  return ref;
};

const ChevronIcon: React.FC = () => (
  <svg aria-hidden="true" viewBox="0 0 12 12"><path d="m3 4.5 3 3 3-3" /></svg>
);

interface InviteAllButtonProps {
  label: string;
  /** Invite all is off: a batch runs, Google is opening, daily invites run, or today's are used. */
  blocked: boolean;
  /** The menu is off while a batch runs or Google is opening. */
  menuBlocked: boolean;
  /** How many people can still be chosen today. */
  chooseLeft: number;
  onInviteAll: () => void;
  onChoose: () => void;
}

/** Invite all, split with a menu to choose people one by one instead (the Invite people picker, for 8080.AI). */
const InviteAllButton: React.FC<InviteAllButtonProps> = ({
  label, blocked, menuBlocked, chooseLeft, onInviteAll, onChoose,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useMenuDismiss(open, setOpen);
  const item = useRef<HTMLButtonElement>(null);

  useEffect(() => { if (open) item.current?.focus(); }, [open]);

  return (
    <div className="refer-way__action refer-split" ref={ref}>
      <button className="refer-way__button refer-split__main" disabled={blocked} onClick={onInviteAll} type="button">
        {label}
      </button>
      <button aria-expanded={open} aria-haspopup="menu" aria-label="More ways to invite"
        className="refer-way__button refer-split__more" disabled={menuBlocked} onClick={() => setOpen(!open)}
        type="button">
        <ChevronIcon />
      </button>
      {open && (
        <div className="refer-split__menu" role="menu">
          <button className="refer-split__item" disabled={chooseLeft === 0} onClick={() => { setOpen(false); onChoose(); }}
            ref={item} role="menuitem" type="button">
            Choose people
            <span>{chooseLeft === 0 ? "You used today's invites." : 'Pick who gets an invite.'}</span>
          </button>
        </div>
      )}
    </div>
  );
};

interface GmailRowProps {
  invite: UseGmailInviteResult;
  status: GmailInviteStatus;
  daily: GmailInviteDaily | null;
  /** The Every day switch: daily invites run, or (until they do) Invite all will also turn them on. */
  everyDay: boolean;
  stopping: boolean;
  onInviteAll: () => void;
  onSwitch: () => void;
}

const inviteAllLabel = (invite: UseGmailInviteResult): string => {
  if (invite.connecting) return 'Opening Google…';

  return invite.starting ? 'Starting…' : 'Invite all';
};

/**
 * Invite contacts from Gmail: Invite all, with the Every day switch (on by default) for the next batch each day, or
 * Choose people from its menu.
 */
const GmailRow: React.FC<GmailRowProps> = ({
  invite, status, daily, everyDay, stopping, onInviteAll, onSwitch,
}) => {
  const busy = (invite.batch !== null && BUSY_STATUSES.has(invite.batch.status)) || invite.starting;
  const running = Boolean(daily?.enabled);
  const blocked = busy || invite.connecting || running || (status.invite_all_remaining === 0 && !everyDay);

  return (
    <section aria-labelledby="refer-gmail-title" className="refer-way">
      <span className="refer-way__icon"><MailIcon /></span>
      <div className="refer-way__copy">
        <div className="refer-way__title">
          <h4 id="refer-gmail-title">Invite contacts</h4>
          <button aria-checked={everyDay} aria-label="Keep inviting every day"
            className={`refer-gmail__switch${everyDay ? ' is-on' : ''}`} disabled={invite.connecting || stopping}
            onClick={onSwitch} role="switch" type="button"
            title={`Send ${status.invite_all_max} more each day, through your whole list and round again.`}>
            <span aria-hidden="true" className="refer-gmail__switch-track"><span className="refer-gmail__switch-thumb" /></span>
            <span aria-hidden="true">Every day</span>
          </button>
        </div>
        <p>{gmailLine(status, daily, everyDay)}</p>
      </div>
      <div className="refer-way__side">
        <WayReward each={`${CREDITS_PER_FRIEND} cr / friend`}
          title={`${CREDITS_PER_FRIEND} credits for each friend who joins, from up to ${status.invite_all_max} invites a day.`}
          total={`${(status.invite_all_max * CREDITS_PER_FRIEND).toLocaleString()} cr / day max`} />
        <InviteAllButton blocked={blocked} chooseLeft={status.remaining_today}
          label={inviteAllLabel(invite)} menuBlocked={busy || invite.connecting}
          onChoose={() => void invite.connect(PLATFORM)} onInviteAll={onInviteAll} />
      </div>
    </section>
  );
};

/** Today's batch on its way, or what the last Invite all did, and anything that went wrong. */
const BatchStatus: React.FC<{ invite: UseGmailInviteResult; errors: Array<string | null | undefined> }> = ({
  invite, errors,
}) => {
  const { batch } = invite;
  const busy = batch !== null && BUSY_STATUSES.has(batch.status);
  const last = busy ? null : recentInviteAll(batch);
  const outcome = last ? outcomeLine(last) : null;
  const shown = [last?.error, invite.error, ...errors].filter((line): line is string => Boolean(line));

  return (
    <>
      {busy && batch && <Progress batch={batch} />}
      {outcome && <p className="refer-gmail__status" role="status">{outcome}</p>}
      {last && invitedNoOne(last) && <NoEmailsFound batch={last} />}
      {shown.map((line) => <p className="refer-error" key={line} role="alert">{line}</p>)}
    </>
  );
};

interface GmailRowsProps {
  invite: UseGmailInviteResult;
  status: GmailInviteStatus;
  /** The other ways to earn, listed under the Gmail row. */
  children?: React.ReactNode;
  /** Invite all was pressed. */
  onStart?: () => void;
  /** Back from Google without the access Invite all needs: what to tick there, and Try again. */
  permission?: boolean;
}

/**
 * Every day starts on: until daily invites run it is what Invite all does (with it, Invite all also turns them on);
 * once they run, turning it off stops them.
 */
const useEveryDay = (
  invite: UseGmailInviteResult, days: ReturnType<typeof useDailyInvites>, onStart?: () => void,
) => {
  const [choice, setChoice] = useState(true);
  const running = Boolean(days.daily?.enabled);
  const onSwitch = () => (running ? days.stop(() => setChoice(false)) : setChoice((on) => !on));
  const onInviteAll = () => {
    onStart?.();
    void (choice ? invite.startDaily() : invite.inviteAll());
  };

  return { everyDay: running || choice, onInviteAll, onSwitch };
};

/** Google's page came back without the access Invite all needs: the help shows until Try again is pressed. */
const usePermissionHelp = (permission: boolean, onInviteAll: () => void) => {
  const [shown, setShown] = useState(permission);
  const retry = () => {
    setShown(false);
    onInviteAll();
  };

  return { shown, retry };
};

/**
 * The Gmail row first (with what to tick on Google's page when its permission was not given), the other ways to
 * earn, then under them: today's progress and the email itself.
 */
const GmailRows: React.FC<GmailRowsProps> = ({ invite, status, children, onStart, permission = false }) => {
  const sample = useSampleEmail();
  const days = useDailyInvites(status.daily ?? null);
  const everyDay = useEveryDay(invite, days, onStart);
  const help = usePermissionHelp(permission, everyDay.onInviteAll);

  return (
    <>
      <GmailRow {...everyDay} daily={days.daily} invite={invite} status={status} stopping={days.stopping} />
      {help.shown && (
        <div className="refer-ways__help">
          <PermissionHelp disabled={invite.connecting || invite.starting} onRetry={help.retry} />
        </div>
      )}
      {children}
      <div className="refer-ways__footer refer-gmail">
        <BatchStatus errors={[days.daily?.error, days.stopError]} invite={invite} />
        {sample.open && <InviteEmailCard preview={sample.preview} to="each contact, by name" />}
        <button aria-expanded={sample.open} className="refer-gmail__link" onClick={sample.toggle} type="button">
          {sample.open ? 'Hide the email' : 'Preview the email'}
        </button>
      </div>
    </>
  );
};

interface GmailInviteAllProps {
  /** Why connecting Gmail failed, when Google sent the user back with an error. */
  notice?: string | null;
  /** The other ways to earn: listed under the Gmail row, or alone when Gmail invites are not offered. */
  children?: React.ReactNode;
  /** Invite all was pressed (the offer after sign-in then offers Close instead of Not now). */
  onStart?: () => void;
  /** Back from Google without the access Invite all needs (Cancel, or a box left unticked). */
  permission?: boolean;
  /** The window Google brings the user back to after Invite all: Refer & earn, or the offer after sign-in. */
  returnTo?: InviteReturn;
  /** Where the window was opened from, kept on the batches Invite all starts. */
  source?: InviteSource;
}

/**
 * Refer & earn's ways to earn, Gmail first: "Invite contacts" (Invite all: one press, then Google unless sign-in
 * already allowed Gmail invites, then the next people in line, with no account and never invited first, get the
 * referral invite from the user's Gmail, up to 400 a day;
 * with the Every day switch, on by default, the same goes on by itself each day through the whole list and round
 * again). The Gmail row shows to accounts with a Google mailbox.
 */
export const GmailInviteAll: React.FC<GmailInviteAllProps> = ({
  notice, children, onStart, permission = false, returnTo = 'refer', source,
}) => {
  // Back without permission: the help says what happened, so the notice is not repeated as an error.
  const invite = useGmailInvite(true, permission ? null : notice, source, returnTo);
  const { status } = invite;

  if (!status?.available || !status.google_account) return <>{children}</>;

  // Daily invites turned on with one press: the row starts again from the status that says so.
  return (
    <GmailRows invite={invite} key={status.daily?.enabled ? 'daily' : 'once'} onStart={onStart}
      permission={permission} status={status}>
      {children}
    </GmailRows>
  );
};
